"use client";

import { useNavigate } from 'react-router-dom';
import BudgetFrequencyField from '@/components/crm/BudgetFrequencyField';
import { budgetLabel, budgetSuffix } from '@/utils/budget';

import { notify, confirmDialog } from "@/components/ui/dialogs";
import { CreatableSelect } from "@/components/ui/creatable-select";
import { DatePicker } from "@/components/ui/date-picker";
import PageHeader from "@/components/layout/PageHeader";
import ViewToggle, { useListView } from "@/components/crm/ViewToggle";
import { Avatar } from "@/components/crm/ListShell";
import Kanban, { type KanbanColumn } from "@/components/crm/Kanban";
import { GridProvider, GridToolbar, StatsStrip, PaginationBar } from "@/components/crm/DataGrid";
import { useCounts, countText } from "@/components/crm/useCounts";
import { toast } from "sonner";
import { apiMessage as apiError } from "@/utils/api";
import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Eye,
  Trash2,
  Target,
  Building,
  Phone,
  Mail,
  MapPin,
  Calendar,
  User,
  DollarSign,
  X,
  AlertCircle,
  Save,
  Clock,
  Edit3,
  PhoneCall,
  Send,
  FileText,
  CalendarCheck,
  Users,
  Star,
  ChevronLeft,
  ChevronRight,
  Building2,
} from "lucide-react";
import { FaProjectDiagram } from "react-icons/fa";
import type { ColumnDef } from "@tanstack/react-table";
import {
  TableProvider,
  DataTableHeader,
  DataTableHeaderGroup,
  DataTableHead,
  DataTableBody,
  DataTableRow,
  DataTableCell,
  TableActions,
  TableDropdown,
  TableDropdownButton,
  TableDropdownMenu,
  TableDropdownItem,
  useTableDropdown,
} from "../../components/ui/data-table";
import { get, put, del, post, getCurrentOrganization, can } from "../../utils/service";
import FloatingDrawer from "@/components/crm/FloatingDrawer";
import StatusProgress from "@/components/crm/StatusProgress";
import AppSelect from "@/components/ui/app-select";
import SearchInput from "@/components/ui/search-input";

interface Lead {
  id: string;
  company_name: string;
  contact_person: string;
  email?: string | null;
  phone?: string;
  source: string;
  priority: string;
  type: string;
  industry: string;
  location: string;
  current_stage: number;
  status: string;
  created_at: string;
  created_by: string;
  first_name: string;
  last_name: string;
  notes?: string;
  decision_makers?: string;
  budget_range?: string;
  budget_frequency?: string;
  currency: string;
  value?: string | number;
  last_contacted_at?: string;
  last_contacted_by?: string;
  contacted_by_first_name?: string;
  contacted_by_last_name?: string;
  lead_source?: string;
  affiliate_potential?: string;
  next_action?: string;
  next_action_date?: string;
  next_action_time?: string;
  meeting_title?: string;
  meeting_description?: string;
  meeting_time_start?: string;
  meeting_time_end?: string;
  created_by_name?: string;
  custom_fields?: Record<string, unknown>;
}

/** A lead as the API returns it (CRM lead resource). */
interface ApiLead {
  id: string;
  name: string;
  first_name: string | null;
  last_name: string | null;
  company_name: string | null;
  email: string | null;
  phone: string | null;
  industry: string | null;
  source: string | null;
  status: string;
  rating: "cold" | "warm" | "hot" | null;
  estimated_value: string | null;
  budget_frequency?: string;
  currency: string;
  service_interest: string | null;
  city: string | null;
  address: string | null;
  description: string | null;
  last_contacted_at: string | null;
  created_at: string;
  created_by: string | null;
  owner?: { id: string; name: string } | null;
  custom_fields?: Record<string, unknown>;
}

const RATING_TO_PRIORITY: Record<string, string> = { hot: "high", warm: "medium", cold: "low" };
const PRIORITY_TO_RATING: Record<string, string> = { high: "hot", medium: "warm", low: "cold" };

/** Maps an API lead onto the fields this page displays. */
const fromApi = (l: ApiLead): Lead => ({
  id: l.id,
  company_name: l.company_name || l.name,
  contact_person: [l.first_name, l.last_name].filter(Boolean).join(" "),
  email: l.email,
  phone: l.phone ?? undefined,
  source: l.source ?? "",
  lead_source: l.source ?? "",
  priority: l.rating ? RATING_TO_PRIORITY[l.rating] : "",
  type: l.service_interest ?? "",
  industry: l.industry ?? "",
  location: l.city || l.address || "",
  current_stage: STAGES.find((s) => s.status === l.status)?.id ?? 1,
  status: l.status,
  created_at: l.created_at,
  created_by: l.created_by ?? "",
  created_by_name: l.owner?.name ?? "",
  first_name: l.first_name ?? "",
  last_name: l.last_name ?? "",
  notes: l.description ?? undefined,
  currency: l.currency,
  value: l.estimated_value ?? undefined,
  budget_frequency: l.budget_frequency ?? 'one_time',
  budget_range: l.estimated_value ?? undefined,
  last_contacted_at: l.last_contacted_at ?? undefined,
  affiliate_potential: typeof l.custom_fields?.affiliate_potential === "string" ? (l.custom_fields.affiliate_potential as string) : undefined,
  custom_fields: l.custom_fields,
});

interface LeadsResponse {
  success: boolean;
  message: string;
  data: ApiLead[];
  pagination: {
    total: number;
    current_page: number;
    per_page: number;
    total_pages: number;
  };
}

interface PaginationState {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
}

// Lead statuses, in the order a lead normally moves. "Converted" is reached through the convert actions.
const STAGES = [
  { id: 1, status: "new", name: "New Lead", color: "bg-gray-100 text-gray-800" },
  { id: 2, status: "contacted", name: "Contacted", color: "bg-blue-100 text-blue-800" },
  { id: 3, status: "qualified", name: "Qualified", color: "bg-green-100 text-green-800" },
  { id: 4, status: "unqualified", name: "Not a fit", color: "bg-yellow-100 text-yellow-800" },
  { id: 5, status: "lost", name: "Lost", color: "bg-red-100 text-red-800" },
  { id: 6, status: "converted", name: "Converted", color: "bg-purple-100 text-purple-800" },
];
const SELECTABLE_STAGES = STAGES.filter((s) => s.status !== "converted");
// The normal path shown on the progress track; "Not a fit" and "Lost" end it early.
const LEAD_JOURNEY = [
  { key: "new", label: "New Lead" },
  { key: "contacted", label: "Contacted" },
  { key: "qualified", label: "Qualified" },
  { key: "converted", label: "Converted" },
];

const PRIORITY_LEVELS = ["low", "medium", "high"];
const LEAD_SOURCES = [
  "website",
  "referral",
  "social_media",
  "cold_call",
  "event",
  "linkedin",
  "google_ads",
  "email_campaign",
  "trade_show",
  "partner",
  "other",
];
const AFFILIATE_POTENTIALS = ["none", "low", "medium", "high", "strategic_partner"];
const NEXT_ACTIONS = [
  { id: "call", name: "Call", icon: "phone" },
  { id: "email", name: "Email", icon: "mail" },
  { id: "send_brochure", name: "Send Brochure", icon: "file" },
  { id: "book_meeting", name: "Book Meeting", icon: "calendar" },
];
const PROJECT_TYPES = [
  "app_development",
  "web_development",
  "mobile_app",
  "web_design",
  "ui_ux_design",
  "figma_design",
  "talent_placement",
  "software_consulting",
  "api_development",
  "database_design",
  "cloud_infrastructure",
  "devops",
  "qa_testing",
  "maintenance_support",
  "custom_software",
];
const INDUSTRIES = [
  "technology",
  "healthcare",
  "finance",
  "retail",
  "education",
  "manufacturing",
  "hospitality",
  "real_estate",
  "transportation",
  "media_entertainment",
  "nonprofit",
  "government",
  "agriculture",
  "energy",
  "telecommunications",
  "other",
];
const SkeletonRow = () => (
  <div className="animate-pulse p-6">
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded mb-2"></div>
    <div className="h-12 bg-gray-100 rounded"></div>
  </div>
);


// Inline Date Picker Component (renders within parent drawer)
const InlineDatePicker = ({
  isOpen,
  onClose,
  value,
  onChange,
  title = "Select Date",
}: {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (date: string) => void;
  title?: string;
}) => {
  const [currentMonth, setCurrentMonth] = useState(() => {
    if (value) {
      const d = new Date(value);
      return new Date(d.getFullYear(), d.getMonth(), 1);
    }
    return new Date(new Date().getFullYear(), new Date().getMonth(), 1);
  });

  const [selectedDate, setSelectedDate] = useState(value);

  useEffect(() => {
    if (isOpen && value) {
      setSelectedDate(value);
      const d = new Date(value);
      setCurrentMonth(new Date(d.getFullYear(), d.getMonth(), 1));
    }
  }, [isOpen, value]);

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  ).getDay();

  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };

  const selectDate = (day: number) => {
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    setSelectedDate(dateStr);
  };

  const handleConfirm = () => {
    onChange(selectedDate);
  };

  const isSelected = (day: number) => {
    if (!selectedDate) return false;
    const dateStr = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    return dateStr === selectedDate;
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth.getMonth() === today.getMonth() &&
      currentMonth.getFullYear() === today.getFullYear()
    );
  };

  const isPastDate = (day: number) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const checkDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
    return checkDate < today;
  };

  const canGoToPrevMonth = () => {
    const today = new Date();
    const firstOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const firstOfDisplayMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return firstOfDisplayMonth > firstOfCurrentMonth;
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.2 }}
      className="absolute inset-0 bg-black z-10 overflow-y-auto"
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Month Navigation */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={prevMonth}
            disabled={!canGoToPrevMonth()}
            className={`p-1.5 rounded-lg transition-colors ${
              canGoToPrevMonth()
                ? "bg-gray-800 text-white hover:bg-gray-700"
                : "bg-gray-900 text-gray-600 cursor-not-allowed"
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <h4 className="text-white text-sm font-semibold">
            {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
          </h4>
          <button
            onClick={nextMonth}
            className="p-1.5 rounded-lg bg-gray-800 text-white hover:bg-gray-700 transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Day Names */}
        <div className="grid grid-cols-7 gap-1 mb-1">
          {dayNames.map((day) => (
            <div key={day} className="text-center text-[10px] text-gray-500 py-1">
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1 mb-4">
          {Array.from({ length: firstDayOfMonth }).map((_, i) => (
            <div key={`empty-${i}`} className="aspect-square" />
          ))}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const past = isPastDate(day);
            return (
              <button
                key={day}
                onClick={() => !past && selectDate(day)}
                disabled={past}
                className={`aspect-square rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                  past
                    ? "text-gray-600 cursor-not-allowed"
                    : isSelected(day)
                    ? "bg-primary text-white"
                    : isToday(day)
                    ? "bg-gray-700 text-white"
                    : "text-gray-300 hover:bg-gray-800"
                }`}
              >
                {day}
              </button>
            );
          })}
        </div>

        {/* Selected Date Display */}
        {selectedDate && (
          <div className="bg-gray-900 rounded-lg p-2 mb-4 text-center">
            <p className="text-[10px] text-gray-400">Selected</p>
            <p className="text-white text-xs font-semibold">
              {new Date(selectedDate).toLocaleDateString("en-US", {
                weekday: "short",
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!selectedDate}
            className="flex-1 px-3 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Confirm
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Inline Time Picker Component (renders within parent drawer)
const InlineTimePicker = ({
  isOpen,
  onClose,
  value,
  onChange,
  title = "Select Time",
  selectedDate = "",
}: {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  onChange: (time: string) => void;
  title?: string;
  selectedDate?: string;
}) => {
  const [selectedHour, setSelectedHour] = useState(() => {
    if (value) {
      const [h] = value.split(":");
      return parseInt(h) || 9;
    }
    return 9;
  });

  const [selectedMinute, setSelectedMinute] = useState(() => {
    if (value) {
      const [, m] = value.split(":");
      return parseInt(m) || 0;
    }
    return 0;
  });

  const [isPM, setIsPM] = useState(() => {
    if (value) {
      const [h] = value.split(":");
      return parseInt(h) >= 12;
    }
    return false;
  });

  useEffect(() => {
    if (isOpen && value) {
      const [h, m] = value.split(":");
      const hour = parseInt(h) || 9;
      setSelectedHour(hour > 12 ? hour - 12 : hour === 0 ? 12 : hour);
      setSelectedMinute(parseInt(m) || 0);
      setIsPM(hour >= 12);
    }
  }, [isOpen, value]);

  const hours = Array.from({ length: 12 }, (_, i) => i + 1);
  const minutes = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  // Check if selected date is today
  const isToday = () => {
    if (!selectedDate) return false;
    const today = new Date();
    const selected = new Date(selectedDate);
    return (
      today.getFullYear() === selected.getFullYear() &&
      today.getMonth() === selected.getMonth() &&
      today.getDate() === selected.getDate()
    );
  };

  // Check if a specific hour is in the past (for today only)
  const isHourPast = (hour: number, pm: boolean) => {
    if (!isToday()) return false;
    const now = new Date();
    let hour24 = hour;
    if (pm && hour !== 12) hour24 = hour + 12;
    else if (!pm && hour === 12) hour24 = 0;
    return hour24 < now.getHours();
  };

  // Check if current selection is valid (not in the past)
  const isTimeValid = () => {
    if (!isToday()) return true;
    const now = new Date();
    let hour24 = selectedHour;
    if (isPM && selectedHour !== 12) hour24 = selectedHour + 12;
    else if (!isPM && selectedHour === 12) hour24 = 0;

    if (hour24 < now.getHours()) return false;
    if (hour24 === now.getHours() && selectedMinute < now.getMinutes()) return false;
    return true;
  };

  const handleConfirm = () => {
    let hour24 = selectedHour;
    if (isPM && selectedHour !== 12) {
      hour24 = selectedHour + 12;
    } else if (!isPM && selectedHour === 12) {
      hour24 = 0;
    }
    const timeStr = `${String(hour24).padStart(2, "0")}:${String(selectedMinute).padStart(2, "0")}`;
    onChange(timeStr);
  };

  const formatDisplayTime = () => {
    return `${selectedHour}:${String(selectedMinute).padStart(2, "0")} ${isPM ? "PM" : "AM"}`;
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.2 }}
      className="absolute inset-0 bg-black z-10 overflow-y-auto"
    >
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-white">{title}</h3>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-white">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Time Display */}
        <div className="bg-gray-900 rounded-xl p-3 mb-4 text-center">
          <p className="text-2xl font-bold text-white">{formatDisplayTime()}</p>
        </div>

        {/* Hour Selection */}
        <div className="mb-3">
          <p className="text-[10px] text-gray-400 mb-1.5">Hour</p>
          <div className="grid grid-cols-6 gap-1.5">
            {hours.map((hour) => {
              const past = isHourPast(hour, isPM);
              return (
                <button
                  key={hour}
                  onClick={() => !past && setSelectedHour(hour)}
                  disabled={past}
                  className={`py-1.5 rounded-lg text-xs font-medium transition-all ${
                    past
                      ? "bg-gray-900 text-gray-600 cursor-not-allowed"
                      : selectedHour === hour
                      ? "bg-primary text-white"
                      : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                  }`}
                >
                  {hour}
                </button>
              );
            })}
          </div>
        </div>

        {/* Minute Selection */}
        <div className="mb-3">
          <p className="text-[10px] text-gray-400 mb-1.5">Minute</p>
          <div className="grid grid-cols-6 gap-1.5">
            {minutes.map((minute) => (
              <button
                key={minute}
                onClick={() => setSelectedMinute(minute)}
                className={`py-1.5 rounded-lg text-xs font-medium transition-all ${
                  selectedMinute === minute
                    ? "bg-primary text-white"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                }`}
              >
                {String(minute).padStart(2, "0")}
              </button>
            ))}
          </div>
        </div>

        {/* AM/PM Selection */}
        <div className="mb-4">
          <p className="text-[10px] text-gray-400 mb-1.5">Period</p>
          <div className="grid grid-cols-2 gap-1.5">
            <button
              onClick={() => setIsPM(false)}
              className={`py-2 rounded-lg text-xs font-medium transition-all ${
                !isPM
                  ? "bg-primary text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              AM
            </button>
            <button
              onClick={() => setIsPM(true)}
              className={`py-2 rounded-lg text-xs font-medium transition-all ${
                isPM
                  ? "bg-primary text-white"
                  : "bg-gray-800 text-gray-300 hover:bg-gray-700"
              }`}
            >
              PM
            </button>
          </div>
        </div>

        {/* Validation Warning */}
        {isToday() && !isTimeValid() && (
          <div className="mb-3 p-2 bg-red-900/30 border border-red-700/50 rounded-lg">
            <p className="text-[10px] text-red-400 text-center">
              Cannot select a time in the past
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 px-3 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!isTimeValid()}
            className="flex-1 px-3 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm
          </button>
        </div>
      </div>
    </motion.div>
  );
};

// Custom Confirmation Popup Component
const ConfirmationPopup = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning",
}: {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: "warning" | "danger" | "info";
}) => {
  const getTypeStyles = () => {
    switch (type) {
      case "danger":
        return {
          icon: "bg-red-500",
          button: "bg-red-600 hover:bg-red-700",
        };
      case "info":
        return {
          icon: "bg-blue-500",
          button: "bg-blue-600 hover:bg-blue-700",
        };
      default:
        return {
          icon: "bg-yellow-500",
          button: "bg-yellow-600 hover:bg-yellow-700",
        };
    }
  };

  const styles = getTypeStyles();

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100000]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-black border border-gray-800 rounded-2xl shadow-2xl z-[100001] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6">
              <div className="flex items-center gap-4 mb-4">
                <div
                  className={`w-12 h-12 ${styles.icon} rounded-full flex items-center justify-center flex-shrink-0`}
                >
                  <AlertCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">{title}</h3>
                  <p className="text-sm text-gray-400 mt-1">{message}</p>
                </div>
              </div>

              <div className="flex items-center gap-3 mt-6">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 text-sm font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
                >
                  {cancelText}
                </button>
                <button
                  onClick={() => {
                    onConfirm();
                    onClose();
                  }}
                  className={`flex-1 px-4 py-3 text-white text-sm font-semibold rounded-lg transition-colors ${styles.button}`}
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body,
  );
};

interface MeetingDetails {
  contact_date: string;
  contact_time_start: string;
  contact_time_end: string;
  meeting_title: string;
  meeting_description: string;
}

const StageChangeModal = ({
  isOpen,
  onClose,
  currentStage,
  newStage,
  onConfirm,
  requiresMeetingDetails,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentStage: number;
  newStage: number;
  onConfirm: (meetingDetails?: MeetingDetails) => void;
  requiresMeetingDetails: boolean;
}) => {
  const [meetingDetails, setMeetingDetails] = useState<MeetingDetails>({
    contact_date: "",
    contact_time_start: "",
    contact_time_end: "",
    meeting_title: "",
    meeting_description: "",
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const getStageName = (stage: number) => {
    return STAGES.find((s) => s.id === stage)?.name || "Unknown";
  };

  const handleConfirm = () => {
    if (requiresMeetingDetails) {
      onConfirm(meetingDetails);
    } else {
      onConfirm();
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "Select date";
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return "Select time";
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    const isPM = hour >= 12;
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${hour12}:${m} ${isPM ? "PM" : "AM"}`;
  };

  const isFormValid =
    !requiresMeetingDetails ||
    (meetingDetails.contact_date &&
      meetingDetails.contact_time_start &&
      meetingDetails.meeting_title &&
      meetingDetails.meeting_description);

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="relative h-full flex flex-col">
        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-xs font-bold text-white">
                Confirm Status Change
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                {requiresMeetingDetails
                  ? "Please provide meeting details"
                  : "Are you sure you want to change the lead status?"}
              </p>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 mb-1">Current Status</p>
                <p className="text-xs font-semibold text-white">
                  {getStageName(currentStage)}
                </p>
              </div>
              <div className="text-gray-400 text-xs">→</div>
              <div>
                <p className="text-xs text-gray-400 mb-1">New Status</p>
                <p className="text-xs font-semibold text-primary">
                  {getStageName(newStage)}
                </p>
              </div>
            </div>
          </div>

          {requiresMeetingDetails && (
            <div className="space-y-4 mb-6">
              <div>
                <label className="text-xs font-semibold text-white mb-2 block">
                  Meeting Title *
                </label>
                <input
                  type="text"
                  value={meetingDetails.meeting_title}
                  onChange={(e) =>
                    setMeetingDetails((prev) => ({
                      ...prev,
                      meeting_title: e.target.value,
                    }))
                  }
                  placeholder="e.g., Initial consultation call"
                  className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                />
              </div>

              <div>
                <label className="text-xs font-semibold text-white mb-2 block">
                  Contact Date *
                </label>
                <button
                  type="button"
                  onClick={() => setShowDatePicker(true)}
                  className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                >
                  <span className={meetingDetails.contact_date ? "text-white" : "text-gray-500"}>
                    {formatDate(meetingDetails.contact_date)}
                  </span>
                  <Calendar className="w-4 h-4 text-gray-400" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Start Time *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowStartTimePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={meetingDetails.contact_time_start ? "text-white" : "text-gray-500"}>
                      {formatTime(meetingDetails.contact_time_start)}
                    </span>
                    <Clock className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    End Time
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowEndTimePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={meetingDetails.contact_time_end ? "text-white" : "text-gray-500"}>
                      {formatTime(meetingDetails.contact_time_end)}
                    </span>
                    <Clock className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-white mb-2 block">
                  Meeting Description *
                </label>
                <textarea
                  value={meetingDetails.meeting_description}
                  onChange={(e) =>
                    setMeetingDetails((prev) => ({
                      ...prev,
                      meeting_description: e.target.value,
                    }))
                  }
                  placeholder="What was discussed during the meeting?"
                  rows={3}
                  className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white resize-none"
                />
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isFormValid}
              className="flex-1 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm Change
            </button>
          </div>
        </div>

        <InlineDatePicker
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          value={meetingDetails.contact_date}
          onChange={(date: string) => {
            setMeetingDetails((prev) => ({ ...prev, contact_date: date }));
            setShowDatePicker(false);
          }}
          title="Select Contact Date"
        />

        <InlineTimePicker
          isOpen={showStartTimePicker}
          onClose={() => setShowStartTimePicker(false)}
          value={meetingDetails.contact_time_start}
          onChange={(time: string) => {
            setMeetingDetails((prev) => ({ ...prev, contact_time_start: time }));
            setShowStartTimePicker(false);
          }}
          title="Select Start Time"
          selectedDate={meetingDetails.contact_date}
        />

        <InlineTimePicker
          isOpen={showEndTimePicker}
          onClose={() => setShowEndTimePicker(false)}
          value={meetingDetails.contact_time_end}
          onChange={(time: string) => {
            setMeetingDetails((prev) => ({ ...prev, contact_time_end: time }));
            setShowEndTimePicker(false);
          }}
          title="Select End Time"
          selectedDate={meetingDetails.contact_date}
        />
      </div>
    </FloatingDrawer>
  );
};

// Next Action Change Modal
interface NextActionDetails {
  action_date: string;
  action_time?: string;
  meeting_title?: string;
  meeting_description?: string;
  meeting_time_start?: string;
  meeting_time_end?: string;
}

const NextActionChangeModal = ({
  isOpen,
  onClose,
  currentAction,
  newAction,
  onConfirm,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentAction: string;
  newAction: string;
  onConfirm: (actionDetails: NextActionDetails) => void;
}) => {
  const [actionDetails, setActionDetails] = useState<NextActionDetails>({
    action_date: "",
    action_time: "",
    meeting_title: "",
    meeting_description: "",
    meeting_time_start: "",
    meeting_time_end: "",
  });
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showActionTimePicker, setShowActionTimePicker] = useState(false);
  const [showStartTimePicker, setShowStartTimePicker] = useState(false);
  const [showEndTimePicker, setShowEndTimePicker] = useState(false);

  const isMeeting = newAction === "book_meeting";

  const getActionName = (action: string) => {
    return NEXT_ACTIONS.find((a) => a.id === action)?.name || action?.replace(/_/g, " ");
  };

  const handleConfirm = () => {
    onConfirm(actionDetails);
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "Select date";
    return new Date(dateStr).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatTime = (timeStr: string) => {
    if (!timeStr) return "Select time";
    const [h, m] = timeStr.split(":");
    const hour = parseInt(h);
    const isPM = hour >= 12;
    const hour12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${hour12}:${m} ${isPM ? "PM" : "AM"}`;
  };

  const isFormValid = isMeeting
    ? actionDetails.action_date &&
      actionDetails.meeting_title &&
      actionDetails.meeting_description &&
      actionDetails.meeting_time_start
    : actionDetails.action_date;

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="relative h-full flex flex-col">
        <div className="p-6 overflow-y-auto flex-1">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
              {newAction === "call" ? (
                <PhoneCall className="w-5 h-5 text-white" />
              ) : newAction === "email" ? (
                <Send className="w-5 h-5 text-white" />
              ) : newAction === "send_brochure" ? (
                <FileText className="w-5 h-5 text-white" />
              ) : (
                <CalendarCheck className="w-5 h-5 text-white" />
              )}
            </div>
            <div>
              <h3 className="text-xs font-bold text-white">
                Set Next Action
              </h3>
              <p className="text-xs text-gray-400 mt-1">
                {isMeeting
                  ? "Please provide meeting details"
                  : "Please set when this action should be done"}
              </p>
            </div>
          </div>

          <div className="bg-gray-900 rounded-lg p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-400 mb-1">Current Action</p>
                <p className="text-xs font-semibold text-white capitalize">
                  {currentAction ? getActionName(currentAction) : "None"}
                </p>
              </div>
              <div className="text-gray-400 text-xs">→</div>
              <div>
                <p className="text-xs text-gray-400 mb-1">New Action</p>
                <p className="text-xs font-semibold text-primary capitalize">
                  {getActionName(newAction)}
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-4 mb-6">
            {isMeeting ? (
              <>
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Meeting Title *
                  </label>
                  <input
                    type="text"
                    value={actionDetails.meeting_title}
                    onChange={(e) =>
                      setActionDetails((prev) => ({
                        ...prev,
                        meeting_title: e.target.value,
                      }))
                    }
                    placeholder="e.g., Product Demo Meeting"
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
                  />
                </div>

                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Meeting Date *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowDatePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={actionDetails.action_date ? "text-white" : "text-gray-500"}>
                      {formatDate(actionDetails.action_date)}
                    </span>
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-semibold text-white mb-2 block">
                      Start Time *
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowStartTimePicker(true)}
                      className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                    >
                      <span className={actionDetails.meeting_time_start ? "text-white" : "text-gray-500"}>
                        {formatTime(actionDetails.meeting_time_start || "")}
                      </span>
                      <Clock className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                  <div>
                    <label className="text-xs font-semibold text-white mb-2 block">
                      End Time
                    </label>
                    <button
                      type="button"
                      onClick={() => setShowEndTimePicker(true)}
                      className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                    >
                      <span className={actionDetails.meeting_time_end ? "text-white" : "text-gray-500"}>
                        {formatTime(actionDetails.meeting_time_end || "")}
                      </span>
                      <Clock className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Meeting Description *
                  </label>
                  <textarea
                    value={actionDetails.meeting_description}
                    onChange={(e) =>
                      setActionDetails((prev) => ({
                        ...prev,
                        meeting_description: e.target.value,
                      }))
                    }
                    placeholder="What will be discussed in this meeting?"
                    rows={3}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white resize-none"
                  />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Action Date *
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowDatePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={actionDetails.action_date ? "text-white" : "text-gray-500"}>
                      {formatDate(actionDetails.action_date)}
                    </span>
                    <Calendar className="w-4 h-4 text-gray-400" />
                  </button>
                </div>

                <div>
                  <label className="text-xs font-semibold text-white mb-2 block">
                    Preferred Time (Optional)
                  </label>
                  <button
                    type="button"
                    onClick={() => setShowActionTimePicker(true)}
                    className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-left flex items-center justify-between text-white hover:border-gray-600 transition-colors"
                  >
                    <span className={actionDetails.action_time ? "text-white" : "text-gray-500"}>
                      {formatTime(actionDetails.action_time || "")}
                    </span>
                    <Clock className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
              </>
            )}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={!isFormValid}
              className="flex-1 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm Action
            </button>
          </div>
        </div>

        <InlineDatePicker
          isOpen={showDatePicker}
          onClose={() => setShowDatePicker(false)}
          value={actionDetails.action_date}
          onChange={(date: string) => {
            setActionDetails((prev) => ({ ...prev, action_date: date }));
            setShowDatePicker(false);
          }}
          title={isMeeting ? "Select Meeting Date" : "Select Action Date"}
        />

        <InlineTimePicker
          isOpen={showActionTimePicker}
          onClose={() => setShowActionTimePicker(false)}
          value={actionDetails.action_time || ""}
          onChange={(time: string) => {
            setActionDetails((prev) => ({ ...prev, action_time: time }));
            setShowActionTimePicker(false);
          }}
          title="Select Preferred Time"
          selectedDate={actionDetails.action_date}
        />

        <InlineTimePicker
          isOpen={showStartTimePicker}
          onClose={() => setShowStartTimePicker(false)}
          value={actionDetails.meeting_time_start || ""}
          onChange={(time: string) => {
            setActionDetails((prev) => ({ ...prev, meeting_time_start: time }));
            setShowStartTimePicker(false);
          }}
          title="Select Start Time"
          selectedDate={actionDetails.action_date}
        />

        <InlineTimePicker
          isOpen={showEndTimePicker}
          onClose={() => setShowEndTimePicker(false)}
          value={actionDetails.meeting_time_end || ""}
          onChange={(time: string) => {
            setActionDetails((prev) => ({ ...prev, meeting_time_end: time }));
            setShowEndTimePicker(false);
          }}
          title="Select End Time"
          selectedDate={actionDetails.action_date}
        />
      </div>
    </FloatingDrawer>
  );
};

const AddLeadForm = ({
  isOpen,
  onClose,
  onSuccess,
  editLead,
  isEditing,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editLead?: Lead | null;
  isEditing?: boolean;
}) => {
  const [formData, setFormData] = useState({
    company_name: "",
    contact_person: "",
    email: "",
    phone: "",
    source: "website",
    priority: "medium",
    budget_range: "",
    budget_frequency: "one_time",
    type: "web_development",
    industry: "technology",
    location: "",
    notes: "",
    decision_makers: "",
    lead_source: "website",
    affiliate_potential: "none",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isEditing && editLead) {
      setFormData({
        company_name: editLead.company_name || "",
        contact_person: editLead.contact_person || "",
        email: editLead.email || "",
        phone: editLead.phone || "",
        source: editLead.source || "website",
        priority: editLead.priority || "medium",
        budget_frequency: editLead.budget_frequency ?? "one_time",
        budget_range: editLead.budget_range ? String(editLead.budget_range) : "",
        type: editLead.type || "web_development",
        industry: editLead.industry || "technology",
        location: editLead.location || "",
        notes: editLead.notes || "",
        decision_makers: editLead.decision_makers || "",
        lead_source: editLead.lead_source || "website",
        affiliate_potential: editLead.affiliate_potential || "",
      });
    } else if (!isEditing) {
      setFormData({
        company_name: "",
        contact_person: "",
        email: "",
        phone: "",
        source: "website",
        priority: "medium",
        budget_range: "",
        budget_frequency: "one_time",
        type: "web_development",
        industry: "technology",
        location: "",
        notes: "",
        decision_makers: "",
        lead_source: "website",
        affiliate_potential: "",
      });
    }
  }, [isEditing, editLead, isOpen]);

  const [errorText, setErrorText] = useState("");

  /** The form's fields, as the leads API expects them. */
  const toApi = () => {
    const [first, ...rest] = formData.contact_person.trim().split(/\s+/);
    const payload: Record<string, unknown> = {
      company_name: formData.company_name || null,
      first_name: first || null,
      last_name: rest.join(" ") || null,
      email: formData.email || null,
      phone: formData.phone || null,
      source: formData.lead_source || formData.source || null,
      rating: PRIORITY_TO_RATING[formData.priority] ?? null,
      service_interest: formData.type || null,
      industry: formData.industry || null,
      city: formData.location || null,
      description: formData.notes || null,
      budget_frequency: formData.budget_frequency,
      estimated_value: formData.budget_range === "" ? null : formData.budget_range,
    };
    if (affiliateField) {
      payload.custom_fields = { ...(editLead?.custom_fields ?? {}), affiliate_potential: formData.affiliate_potential || null };
    }
    return payload;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorText("");

    try {
      if (isEditing && editLead) {
        await put(`/leads/${editLead.id}`, toApi());
        notify("Lead updated");
      } else {
        await post("/leads", toApi());
        notify("Lead created");
      }
      onSuccess();
      onClose();
    } catch (error: any) {
      const errors = error?.response?.data?.errors as Record<string, string[]> | undefined;
      setErrorText(errors ? Object.values(errors).flat()[0] : error?.response?.data?.message || "Could not save the lead.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const [typeOptions, setTypeOptions] = useState<string[]>(PROJECT_TYPES);
  const [industryOptions, setIndustryOptions] = useState<string[]>(INDUSTRIES);
  const [sourceOptions, setSourceOptions] = useState<string[]>(LEAD_SOURCES);
  // "Affiliate potential" is an organization custom field (kept from the original system).
  const [affiliateField, setAffiliateField] = useState<{ options: string[] } | null>(null);
  useEffect(() => {
    // Built-ins plus anything used before, so earlier values appear in the list next time.
    get<{ data: { service_interests: string[]; industries: string[]; sources: string[] } }>("/leads-options")
      .then((r) => {
        setTypeOptions([...new Set([...PROJECT_TYPES, ...r.data.service_interests])]);
        setIndustryOptions([...new Set([...INDUSTRIES, ...r.data.industries])]);
        setSourceOptions([...new Set([...LEAD_SOURCES, ...r.data.sources])]);
      })
      .catch(() => undefined);
    get<{ data: { fields: Array<{ key: string; options: string[] | null }> } }>("/custom-fields?entity=lead")
      .then((r) => {
        const f = r.data.fields.find((x) => x.key === "affiliate_potential");
        setAffiliateField(f ? { options: f.options ?? AFFILIATE_POTENTIALS } : null);
      })
      .catch(() => undefined);
  }, []);
  const currency = editLead?.currency || getCurrentOrganization()?.base_currency || "";

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  return (
    <FloatingDrawer isOpen={isOpen} onClose={onClose}>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">
            {isEditing ? "Edit Lead" : "Add New Lead"}
          </h2>
          <p className="text-xs text-gray-400 mt-1">
            {isEditing ? "Update lead information" : "Create a new sales lead"}
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex-1 overflow-y-auto p-6 space-y-4"
      >
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Company Name *
          </label>
          <input
            type="text"
            required
            value={formData.company_name}
            onChange={(e) => handleChange("company_name", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Contact Person *
          </label>
          <input
            type="text"
            required
            value={formData.contact_person}
            onChange={(e) => handleChange("contact_person", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Email {formData.phone ? "(optional)" : "*"}
            </label>
            <input
              type="email"
              required={!formData.phone}
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Phone {formData.email ? "(optional)" : "*"}
            </label>
            <input
              type="tel"
              required={!formData.email}
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              placeholder="e.g. +256 700 000000"
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>
        <p className="-mt-2 text-[11px] text-gray-500">Provide an email or a phone number — at least one is required.</p>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Service Interest *
            </label>
            <CreatableSelect
              value={formData.type}
              onChange={(v) => handleChange("type", v)}
              options={typeOptions}
              addLabel="Add new type…"
              placeholder="e.g. Cloud hosting"
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
              required
            />
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Industry *
            </label>
            <CreatableSelect
              value={formData.industry}
              onChange={(v) => handleChange("industry", v)}
              options={industryOptions}
              addLabel="Add new industry…"
              placeholder="e.g. Marine logistics"
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Lead Source
            </label>
            <AppSelect
              value={formData.lead_source}
              onChange={(e) => handleChange("lead_source", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {sourceOptions.map((source) => (
                <option key={source} value={source}>
                  {source
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </AppSelect>
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Rating
            </label>
            <AppSelect
              value={formData.priority}
              onChange={(e) => handleChange("priority", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              <option value="">Not rated</option>
              {PRIORITY_LEVELS.map((priority) => (
                <option key={priority} value={priority}>
                  {{ high: "Hot", medium: "Warm", low: "Cold" }[priority]}
                </option>
              ))}
            </AppSelect>
          </div>
        </div>

        {affiliateField && (
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Affiliate Potential
            </label>
            <AppSelect
              value={formData.affiliate_potential}
              onChange={(e) => handleChange("affiliate_potential", e.target.value)}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              <option value="">Not set</option>
              {affiliateField.options.map((potential) => (
                <option key={potential} value={potential}>
                  {potential
                    .replace(/_/g, " ")
                    .replace(/\b\w/g, (l) => l.toUpperCase())}
                </option>
              ))}
            </AppSelect>
          </div>
        )}

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            {budgetLabel(formData.budget_frequency)} {currency && `(${currency})`}
          </label>
          <input
            type="number"
            min={0}
            step="0.01"
            value={formData.budget_range}
            onChange={(e) => handleChange("budget_range", e.target.value)}
            placeholder="What this lead could be worth"
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-gray-500"
          />
        </div>

        <BudgetFrequencyField value={formData.budget_frequency} onChange={(value) => handleChange('budget_frequency', value)} className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-white" labelClassName="text-xs font-semibold text-white mb-2 block" />
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Location
          </label>
          <input
            type="text"
            value={formData.location}
            onChange={(e) => handleChange("location", e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          />
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Notes
          </label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleChange("notes", e.target.value)}
            rows={3}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white resize-none"
          />
        </div>
      </form>

      <div className="p-6 border-t border-gray-800 space-y-3">
        {errorText && <p className="text-xs text-red-400">{errorText}</p>}
        <button
          onClick={handleSubmit}
          disabled={
            isSubmitting ||
            !formData.company_name ||
            !formData.contact_person ||
            (!formData.email.trim() && !formData.phone.trim()) ||
            !formData.type.trim() ||
            !formData.industry.trim()
          }
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isSubmitting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              {isEditing ? "Updating..." : "Creating..."}
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              {isEditing ? "Update Lead" : "Create Lead"}
            </>
          )}
        </button>

        <button
          onClick={onClose}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
      </div>
    </FloatingDrawer>
  );
};

const SERVICE_TYPES = [
  "social_media_management",
  "content_creation",
  "seo",
  "paid_advertising",
  "app_deployment",
  "talent_placement",
  "web_development",
  "consulting",
  "other",
];

const LeadDrawer = ({
  lead,
  isOpen,
  onClose,
  onStageChange,
  onNextActionChange,
  onConvert,
  onConvertToClient,
  onDelete,
  onEdit,
}: {
  lead: Lead | null;
  isOpen: boolean;
  onClose: () => void;
  onStageChange: (leadId: string, stage: number, meetingDetails?: MeetingDetails) => void;
  onNextActionChange: (leadId: string, action: string, actionDetails: NextActionDetails) => void;
  onConvert: (leadId: string, value: number, frequency: string) => void;
  onConvertToClient: (leadId: string, contractData: {
    service_type: string;
    start_date: string;
    duration_months: number;
    value?: number;
    billing_cycle: string;
  }) => void;
  onDelete: (leadId: string) => void;
  onEdit: (lead: Lead) => void;
}) => {
  const [currentView, setCurrentView] = useState<
    "details" | "convert" | "convertToClient" | "stageConfirm" | "actionConfirm"
  >("details");
  const [selectedStage, setSelectedStage] = useState(1);
  const [selectedAction, setSelectedAction] = useState("");
  const [dealFrequency, setDealFrequency] = useState('one_time');
  const [projectValue, setProjectValue] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [clientForm, setClientForm] = useState({
    service_type: SERVICE_TYPES[0],
    start_date: new Date().toISOString().slice(0, 10),
    duration_months: "6",
    value: "",
    billing_cycle: "monthly",
  });
  const [isConvertingToClient, setIsConvertingToClient] = useState(false);
  const [nextStep, setNextStep] = useState<{ action: string; title: string; at: string; description?: string | null } | null>(null);

  // The next step is the earliest open task or planned meeting linked to this lead.
  useEffect(() => {
    if (!isOpen || !lead) return;
    let live = true;
    setNextStep(null);
    Promise.all([
      get<{ data: Array<{ title: string; due_at: string | null }> }>(`/tasks?related_type=lead&related_id=${lead.id}&view=open&sort=due_at&page_size=1`).catch(() => ({ data: [] })),
      get<{ data: Array<{ title: string; starts_at: string | null; body: string | null }> }>(`/activities?subject_type=lead&subject_id=${lead.id}&type=meeting&status=planned&sort=starts_at&page_size=1`).catch(() => ({ data: [] })),
    ]).then(([tasks, meetings]) => {
      if (!live) return;
      const task = tasks.data[0];
      const meeting = meetings.data[0];
      const candidates = [
        task?.due_at ? { action: /^call/i.test(task.title) ? "call" : /^email/i.test(task.title) ? "email" : /brochure/i.test(task.title) ? "send_brochure" : "task", title: task.title, at: task.due_at } : null,
        meeting?.starts_at ? { action: "book_meeting", title: meeting.title, at: meeting.starts_at, description: meeting.body } : null,
      ].filter(Boolean) as Array<{ action: string; title: string; at: string; description?: string | null }>;
      candidates.sort((a, b) => a.at.localeCompare(b.at));
      setNextStep(candidates[0] ?? null);
    });
    return () => { live = false; };
  }, [isOpen, lead]);

  useEffect(() => {
    if (isOpen) {
      setCurrentView("details");
      setProjectValue(lead?.value != null ? String(lead.value) : '');
      setDealFrequency(lead?.budget_frequency ?? 'one_time');
      setSelectedStage(lead?.current_stage || 1);
      setSelectedAction(lead?.next_action || "");
      setClientForm({
        service_type: SERVICE_TYPES[0],
        start_date: new Date().toISOString().slice(0, 10),
        duration_months: "6",
        value: lead?.value ? String(lead.value) : "",
        billing_cycle: lead?.budget_frequency ?? "one_time",
      });
    }
  }, [isOpen, lead]);

  if (!lead) return null;

  const handleStageChange = (newStage: number) => {
    setSelectedStage(newStage);
    setCurrentView("stageConfirm");
  };

  const handleNextActionChange = (newAction: string) => {
    setSelectedAction(newAction);
    setCurrentView("actionConfirm");
  };

  const confirmStageChange = (meetingDetails?: MeetingDetails) => {
    onStageChange(lead.id, selectedStage, meetingDetails);
    setCurrentView("details");
  };

  const confirmNextActionChange = (actionDetails: NextActionDetails) => {
    onNextActionChange(lead.id, selectedAction, actionDetails);
    setCurrentView("details");
  };

  const handleConvert = async () => {
    if (!projectValue || isNaN(parseFloat(projectValue))) {
      notify("Please enter a valid deal value");
      return;
    }

    setIsConverting(true);
    try {
      onConvert(lead.id, parseFloat(projectValue), dealFrequency);
      onClose();
    } catch (error) {
      console.error("Error converting lead:", error);
    } finally {
      setIsConverting(false);
    }
  };

  const handleConvertToClient = async () => {
    if (!clientForm.start_date || !clientForm.duration_months) {
      notify("Please fill in the contract start date and duration");
      return;
    }

    setIsConvertingToClient(true);
    try {
      onConvertToClient(lead.id, {
        service_type: clientForm.service_type,
        start_date: clientForm.start_date,
        duration_months: parseInt(clientForm.duration_months, 10),
        value: clientForm.value ? parseFloat(clientForm.value) : undefined,
        billing_cycle: clientForm.billing_cycle,
      });
      onClose();
    } catch (error) {
      console.error("Error converting lead to client:", error);
    } finally {
      setIsConvertingToClient(false);
    }
  };

  const getStageInfo = (stage: number) => {
    return STAGES.find((s) => s.id === stage) || STAGES[0];
  };

  const isContactedStage = selectedStage === 2 && lead.current_stage !== 2;

  const renderDetailsView = () => (
    <>
      <div className="flex items-center justify-between p-6 border-b border-gray-800">
        <div>
          <h2 className="text-xs font-bold text-white">Lead Details</h2>
          <p className="text-xs text-gray-400 mt-1">{lead.company_name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <StatusProgress
          dark
          steps={LEAD_JOURNEY}
          current={["unqualified", "lost"].includes(lead.status) ? "new" : lead.status}
          exit={lead.status === "lost" ? { label: "Lost" } : lead.status === "unqualified" ? { label: "Not a fit" } : null}
          onSelect={lead.status === "converted" ? undefined : (key) => { const st = STAGES.find((x) => x.status === key); if (st) handleStageChange(st.id); }}
          canSelect={(key) => key !== "converted"}
        />
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Lead Status
            </label>
            <AppSelect
              value={lead.current_stage}
              disabled={lead.status === "converted"}
              onChange={(e) => handleStageChange(parseInt(e.target.value))}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              {(lead.status === "converted" ? STAGES : SELECTABLE_STAGES).map((stage) => (
                <option key={stage.id} value={stage.id} disabled={stage.status === "converted"}>
                  {stage.name}
                </option>
              ))}
            </AppSelect>
          </div>

          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Status
            </label>
            <div
              className={`px-3 py-2 text-xs font-semibold rounded-lg ${
                getStageInfo(lead.current_stage).color
              }`}
            >
              {getStageInfo(lead.current_stage).name}
            </div>
          </div>
        </div>

        {/* Next Action Dropdown - Like Current Stage */}
        <div>
          <label className="text-xs font-semibold text-white mb-2 block">
            Next Action
          </label>
          <AppSelect
            value=""
            onChange={(e) => e.target.value && handleNextActionChange(e.target.value)}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            <option value="">{nextStep ? "Schedule another step…" : "Schedule a next step…"}</option>
            {NEXT_ACTIONS.map((action) => (
              <option key={action.id} value={action.id}>
                {action.name}
              </option>
            ))}
          </AppSelect>
        </div>

        {/* Next Step Display */}
        {nextStep && (
          <div className="bg-gradient-to-r from-primary/40 to-blue-primary/20 border border-primary/50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                {nextStep.action === "call" ? (
                  <PhoneCall className="w-3 h-3 text-white" />
                ) : nextStep.action === "email" ? (
                  <Send className="w-3 h-3 text-white" />
                ) : nextStep.action === "send_brochure" ? (
                  <FileText className="w-3 h-3 text-white" />
                ) : (
                  <CalendarCheck className="w-3 h-3 text-white" />
                )}
              </div>
              <div className="flex-1">
                <p className="text-xs font-bold text-primary uppercase tracking-wide mb-1">
                  Next Step
                </p>
                <p className="text-sm font-semibold text-white">{nextStep.title}</p>
                <p className="text-xs text-gray-300 mt-1">
                  <span className="text-gray-400">Scheduled for:</span>{" "}
                  {new Date(nextStep.at).toLocaleString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "numeric",
                    minute: "2-digit",
                  })}
                </p>
                {nextStep.description && (
                  <p className="text-xs text-gray-400 mt-2 italic">
                    "{nextStep.description}"
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <Building className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Company</p>
              <p className="text-xs text-gray-400">{lead.company_name}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Target className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Service Interest</p>
              <p className="text-xs text-gray-400 capitalize">
                {lead.type?.replace(/_/g, " ")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Building className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Industry</p>
              <p className="text-xs text-gray-400 capitalize">
                {lead.industry?.replace(/_/g, " ")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <User className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Contact Person</p>
              <p className="text-xs text-gray-400">{lead.contact_person}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Mail className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Email</p>
              <p className="text-xs text-gray-400">{lead.email || "—"}</p>
            </div>
          </div>

          {lead.phone && (
            <div className="flex items-center gap-3">
              <Phone className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Phone</p>
                <p className="text-xs text-gray-400">{lead.phone}</p>
              </div>
            </div>
          )}

          {lead.location && (
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Location</p>
                <p className="text-xs text-gray-400">{lead.location}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Target className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Source</p>
              <p className="text-xs text-gray-400 capitalize">
                {lead.source?.replace(/_/g, " ")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Star className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Rating</p>
              <p className={`text-xs capitalize ${
                lead.priority === "high"
                  ? "text-red-400"
                  : lead.priority === "medium"
                  ? "text-yellow-400"
                  : "text-green-400"
              }`}>
                {({ high: "Hot", medium: "Warm", low: "Cold" } as Record<string, string>)[lead.priority] || "Not rated"}
              </p>
            </div>
          </div>

          {lead.affiliate_potential && (
            <div className="flex items-center gap-3">
              <Users className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">Affiliate Potential</p>
                <p className="text-xs text-gray-400 capitalize">
                  {lead.affiliate_potential?.replace(/_/g, " ")}
                </p>
              </div>
            </div>
          )}

          {lead.value && (
            <div className="flex items-center gap-3">
              <DollarSign className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">{budgetLabel(lead.budget_frequency)}</p>
                <p className="text-xs text-gray-400">{lead.currency} {Number(lead.value).toLocaleString()}{budgetSuffix(lead.budget_frequency)}</p>
              </div>
            </div>
          )}

          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-gray-400" />
            <div>
              <p className="text-xs font-semibold text-white">Created</p>
              <p className="text-xs text-gray-400">
                {new Date(lead.created_at).toLocaleDateString()}
                {lead.created_by_name && ` · owned by ${lead.created_by_name}`}
              </p>
            </div>
          </div>

          {lead.last_contacted_at && (
            <div className="flex items-center gap-3">
              <Clock className="w-4 h-4 text-gray-400" />
              <div>
                <p className="text-xs font-semibold text-white">
                  Last Contacted
                </p>
                <p className="text-xs text-gray-400">
                  {new Date(lead.last_contacted_at).toLocaleString()}
                </p>
              </div>
            </div>
          )}
        </div>

        {lead.notes && (
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              Notes
            </label>
            <p className="text-xs text-gray-400 bg-gray-900 p-3 rounded-lg">
              {lead.notes}
            </p>
          </div>
        )}
      </div>

      <div className="p-3 border-t border-gray-800 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => onEdit(lead)}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
          >
            <Edit3 className="w-4 h-4" />
            Edit Lead
          </button>

          <button
            onClick={() => setCurrentView("convert")}
            disabled={lead.status === "converted"}
            className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FaProjectDiagram className="w-4 h-4" />
            Convert to Deal
          </button>
        </div>

        <button
          onClick={() => setCurrentView("convertToClient")}
          disabled={lead.status === "converted"}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-xs rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Building2 className="w-4 h-4" />
          Convert to Account (with contract)
        </button>

        <button
          onClick={() => setShowDeleteConfirm(true)}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700 transition-colors"
        >
          <Trash2 className="w-4 h-4" />
          Delete Lead
        </button>
      </div>

      <ConfirmationPopup
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => onDelete(lead.id)}
        title="Delete Lead"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </>
  );

  const renderConvertView = () => (
    <>
      <div className="flex items-center gap-3 p-6 border-b border-gray-800">
        <button
          onClick={() => setCurrentView("details")}
          className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div>
          <h2 className="text-xs font-bold text-white">Convert to Deal</h2>
          <p className="text-xs text-gray-400 mt-1">{lead.company_name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-6">
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">
              {budgetLabel(dealFrequency)} ({lead.currency})
            </label>
            <input
              type="number"
              value={projectValue}
              onChange={(e) => setProjectValue(e.target.value)}
              placeholder="Expected value of the deal"
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-gray-500"
            />
          </div>

          <BudgetFrequencyField value={dealFrequency} onChange={setDealFrequency} className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg text-white" labelClassName="text-xs font-semibold text-white mb-2 block" />
          <div className="bg-gray-900 rounded-lg p-4">
            <p className="text-xs text-gray-400 mb-2">An account, a contact and an open deal will be created from this lead. The lead itself is kept.</p>
            <p className="text-xs text-white">{lead.company_name}</p>
            <p className="text-xs text-gray-400 mt-1">{lead.contact_person}</p>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={() => setCurrentView("details")}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConvert}
          disabled={!projectValue || isConverting}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white text-xs font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isConverting ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Converting...
            </>
          ) : (
            <>
              <FaProjectDiagram className="w-4 h-4" />
              Convert to Deal
            </>
          )}
        </button>
      </div>
    </>
  );

  const renderConvertToClientView = () => (
    <>
      <div className="flex items-center gap-3 p-6 border-b border-gray-800">
        <button
          onClick={() => setCurrentView("details")}
          className="p-2 rounded-lg text-gray-400 hover:bg-gray-800 transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <div>
          <h2 className="text-xs font-bold text-white">Convert to Account</h2>
          <p className="text-xs text-gray-400 mt-1">{lead.company_name}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-4">
        <div className="bg-gray-900 rounded-lg p-4 mb-2">
          <p className="text-xs text-gray-400 mb-2">An account and a contact will be created from this lead, with this contract as the account's first. The lead itself is kept.</p>
          <p className="text-xs text-white">{lead.company_name}</p>
          <p className="text-xs text-gray-400 mt-1">{lead.contact_person}</p>
        </div>

        <div>
          <label className="text-xs font-semibold text-white mb-2 block">Service Type *</label>
          <AppSelect
            value={clientForm.service_type}
            onChange={(e) => setClientForm((prev) => ({ ...prev, service_type: e.target.value }))}
            className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
          >
            {SERVICE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}
              </option>
            ))}
          </AppSelect>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Start Date *</label>
            <DatePicker dark value={clientForm.start_date} onChange={(v) => setClientForm((prev) => ({ ...prev, start_date: v }))} required />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Duration (months) *</label>
            <input
              type="number"
              min={1}
              value={clientForm.duration_months}
              onChange={(e) => setClientForm((prev) => ({ ...prev, duration_months: e.target.value }))}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Contract Value ({lead.currency})</label>
            <input
              type="number"
              value={clientForm.value}
              onChange={(e) => setClientForm((prev) => ({ ...prev, value: e.target.value }))}
              placeholder="Per billing cycle"
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white placeholder:text-gray-500"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-white mb-2 block">Billing Cycle</label>
            <AppSelect
              value={clientForm.billing_cycle}
              onChange={(e) => setClientForm((prev) => ({ ...prev, billing_cycle: e.target.value }))}
              className="w-full px-3 py-2 text-xs bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-white"
            >
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="one_time">One-time</option>
            </AppSelect>
          </div>
        </div>
      </div>

      <div className="p-6 border-t border-gray-800 space-y-3">
        <button
          onClick={() => setCurrentView("details")}
          className="w-full px-4 py-2 text-xs font-semibold text-gray-300 bg-gray-800 rounded-lg hover:bg-gray-700 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConvertToClient}
          disabled={!clientForm.start_date || !clientForm.duration_months || isConvertingToClient}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white text-xs font-semibold rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {isConvertingToClient ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              Converting...
            </>
          ) : (
            <>
              <Building2 className="w-4 h-4" />
              Convert to Account
            </>
          )}
        </button>
      </div>
    </>
  );

  return (
    <>
      <FloatingDrawer
        isOpen={isOpen && currentView !== "stageConfirm" && currentView !== "actionConfirm"}
        onClose={onClose}
      >
        {currentView === "details" && renderDetailsView()}
        {currentView === "convert" && renderConvertView()}
        {currentView === "convertToClient" && renderConvertToClientView()}
      </FloatingDrawer>

      <StageChangeModal
        isOpen={currentView === "stageConfirm"}
        onClose={() => setCurrentView("details")}
        currentStage={lead.current_stage}
        newStage={selectedStage}
        onConfirm={confirmStageChange}
        requiresMeetingDetails={isContactedStage}
      />

      <NextActionChangeModal
        isOpen={currentView === "actionConfirm"}
        onClose={() => setCurrentView("details")}
        currentAction={lead.next_action || ""}
        newAction={selectedAction}
        onConfirm={confirmNextActionChange}
      />
    </>
  );
};

function ActionsCell({
  onView,
  onDelete,
}: {
  onView: () => void;
  onDelete: () => void;
}) {
  const { isOpen, position, handleOpen, handleClose } = useTableDropdown();

  return (
    <TableActions>
      <TableDropdown>
        <TableDropdownButton onClick={handleOpen} />
        <TableDropdownMenu
          isOpen={isOpen}
          onClose={handleClose}
          position={position}
        >
          <TableDropdownItem
            onClick={() => {
              onView();
              handleClose();
            }}
          >
            <Eye className="w-3 h-3" />
            View Details
          </TableDropdownItem>
          <TableDropdownItem
            onClick={() => {
              onDelete();
              handleClose();
            }}
            className="text-red-600 hover:bg-red-50"
          >
            <Trash2 className="w-3 h-3" />
            Delete
          </TableDropdownItem>
        </TableDropdownMenu>
      </TableDropdown>
    </TableActions>
  );
}

const BOARD_DOTS: Record<string, string> = { new: "bg-orange-500", contacted: "bg-blue-500", qualified: "bg-green-500", unqualified: "bg-yellow-400", lost: "bg-red-500", converted: "bg-purple-500" };
const RATING_CHIP: Record<string, string> = { high: "bg-red-50 text-red-600", medium: "bg-orange-50 text-orange-600", low: "bg-sky-50 text-sky-600" };
const RATING_LABEL: Record<string, string> = { high: "Hot", medium: "Warm", low: "Cold" };

/** "Today 10:30 PM", "Yesterday 9:05 AM" or "12 Sep 4:00 PM". */
const boardTime = (iso: string) => {
  const d = new Date(iso);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const days = Math.round((new Date(d).setHours(0, 0, 0, 0) - today.getTime()) / 86_400_000);
  const t = d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  return days === 0 ? `Today ${t}` : days === -1 ? `Yesterday ${t}` : `${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })} ${t}`;
};

/** Leads as a board: one column per status, drag (or "Move to") to change a lead's status. */
function LeadsBoard({ refreshKey, onOpen, onMoved }: { refreshKey: number; onOpen: (lead: Lead) => void; onMoved: () => void }) {
  const [columns, setColumns] = useState<KanbanColumn<Lead>[]>([]);
  const [loading, setLoading] = useState(true);
  const editable = can("leads.update");

  useEffect(() => {
    let live = true;
    setLoading(true);
    get<{ data: { columns: Array<{ status: string; count: number; items: ApiLead[] }> } }>("/leads-board", { per_column: 50, sort: "-created_at" })
      .then((r) => {
        if (!live) return;
        const byStatus = new Map(r.data.columns.map((c) => [c.status, c]));
        setColumns(STAGES.map((st) => ({
          id: st.status, title: st.name, dot: BOARD_DOTS[st.status] ?? "bg-gray-400",
          items: (byStatus.get(st.status)?.items ?? []).map(fromApi), count: byStatus.get(st.status)?.count ?? 0,
          droppable: st.status !== "converted",
        })));
      })
      .catch((error) => { if (live) notify(apiError(error, "Could not load the board.")); })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [refreshKey]);

  const move = async (lead: Lead, _from: string, to: string) => {
    try {
      await put(`/leads/${lead.id}`, { status: to });
      toast.success(`${lead.contact_person || lead.company_name} moved to ${STAGES.find((st) => st.status === to)?.name ?? to}`);
      onMoved();
    } catch (error) {
      notify(apiError(error, "Could not change the status."));
      return false;
    }
  };

  return (
    <Kanban
      columns={columns}
      loading={loading}
      noun="Lead"
      emptyText="No leads"
      getId={(l) => l.id}
      onOpen={onOpen}
      onMove={move}
      canMove={(l) => editable && l.status !== "converted"}
      renderCard={(l) => {
        const name = l.contact_person || l.company_name;
        return (
          <div className="pr-6">
            <div className="flex items-center gap-3">
              <Avatar name={name} />
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-gray-900">{name}</p>
                <p className="truncate text-xs text-gray-500">{l.contact_person && l.company_name && l.company_name !== name ? `${l.company_name} · ` : ""}{boardTime(l.created_at)}</p>
              </div>
            </div>
            <div className="-mr-6 mt-3 space-y-1.5 border-t border-gray-100 pt-3 text-xs text-gray-600">
              <p className="flex items-center gap-2 truncate"><Phone className="h-3.5 w-3.5 shrink-0 text-gray-400" />{l.phone || "No phone"}</p>
              <p className="flex items-center gap-2 truncate"><Mail className="h-3.5 w-3.5 shrink-0 text-gray-400" />{l.email || "No email"}</p>
            </div>
            <div className="-mr-6 mt-3 flex flex-wrap gap-1.5">
              {l.priority && <span className={`rounded-md px-2 py-1 text-[11px] font-medium ${RATING_CHIP[l.priority] ?? "bg-gray-100 text-gray-600"}`}>{RATING_LABEL[l.priority] ?? l.priority}</span>}
              {l.source && <span className="rounded-md bg-gray-100 px-2 py-1 text-[11px] font-medium capitalize text-gray-600">{l.source.replace(/_/g, " ")}</span>}
              {l.value && Number(l.value) > 0 && <span className="rounded-md bg-green-50 px-2 py-1 text-[11px] font-medium text-green-700">{l.currency} {Number(l.value).toLocaleString()}{budgetSuffix(l.budget_frequency)}</span>}
            </div>
          </div>
        );
      }}
    />
  );
}

function LeadsPage() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [view, setView] = useListView("leads", ["board", "table"] as const, "board");
  const [boardKey, setBoardKey] = useState(0);
  const listLoaded = useRef(false); // the board loads itself the first time; later list reloads refresh it
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [ratingFilter, setRatingFilter] = useState("");
  const [sort, setSort] = useState("-created_at");
  useEffect(() => { const t = setTimeout(() => setSearchQuery(searchInput.trim()), 300); return () => clearTimeout(t); }, [searchInput]);
  const { counts, loading: countsLoading } = useCounts("/leads", {
    open: { status: "new,contacted,qualified" }, fresh: { status: "new" }, qualified: { status: "qualified" }, converted: { status: "converted" },
  }, boardKey);
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [showLeadDrawer, setShowLeadDrawer] = useState(false);
  const [showAddLead, setShowAddLead] = useState(false);
  const [isEditingLead, setIsEditingLead] = useState(false);
  const [editingLead, setEditingLead] = useState<Lead | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [leadToDelete, setLeadToDelete] = useState<string | null>(null);
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    per_page: 15,
    total: 0,
    total_pages: 0,
  });

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      // Converted leads live on as accounts and deals; this list shows leads still being worked.
      const response = await get<LeadsResponse>("/leads", {
        page: pagination.page,
        page_size: pagination.per_page,
        status: statusFilter === "all" ? "new,contacted,qualified,unqualified,lost" : statusFilter,
        rating: ratingFilter || undefined,
        search: searchQuery || undefined,
        sort,
      });
      if (response.success) {
        setLeads(response.data.map(fromApi));
        if (listLoaded.current) setBoardKey((k) => k + 1);
        listLoaded.current = true;
        setPagination((prev) => ({
          ...prev,
          total: response.pagination.total,
          total_pages: response.pagination.total_pages,
        }));
      }
    } catch (error) {
      console.error("Error fetching leads:", error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, statusFilter, ratingFilter, searchQuery, sort]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  // New filters start again from the first page.
  useEffect(() => { setPagination((prev) => ({ ...prev, page: 1 })); }, [statusFilter, ratingFilter, searchQuery, sort]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const apiMessage = (error: any, fallback: string) => {
    const errors = error?.response?.data?.errors as Record<string, string[]> | undefined;
    return (errors && Object.values(errors).flat()[0]) || error?.response?.data?.message || fallback;
  };

  const leadName = (leadId: string) => leads.find((l) => l.id === leadId)?.company_name ?? "lead";

  /** Local date + time (from the pickers) as an ISO timestamp. */
  const at = (date: string, time?: string) => new Date(`${date}T${time || "09:00"}`).toISOString();

  const handleStageChange = async (
    leadId: string,
    stage: number,
    meetingDetails?: MeetingDetails
  ) => {
    const status = STAGES.find((s) => s.id === stage)?.status ?? "new";
    try {
      await put(`/leads/${leadId}`, { status });

      // Moving to "Contacted" records the conversation as a completed meeting on the lead.
      if (meetingDetails) {
        await post("/activities", {
          type: "meeting",
          status: "completed",
          title: meetingDetails.meeting_title,
          body: meetingDetails.meeting_description,
          starts_at: at(meetingDetails.contact_date, meetingDetails.contact_time_start),
          ends_at: meetingDetails.contact_time_end ? at(meetingDetails.contact_date, meetingDetails.contact_time_end) : null,
          subject_type: "lead",
          subject_id: leadId,
        });
      }

      notify(`Status changed to ${STAGES.find((s) => s.id === stage)?.name}`);
      if (selectedLead?.id === leadId) {
        setSelectedLead((prev) => (prev ? { ...prev, current_stage: stage, status } : null));
      }
      fetchLeads();
    } catch (error) {
      notify(apiMessage(error, "Could not change the status."));
    }
  };

  const handleNextActionChange = async (
    leadId: string,
    action: string,
    actionDetails: NextActionDetails
  ) => {
    const company = leadName(leadId);
    try {
      if (action === "book_meeting") {
        // A meeting goes on the calendar as a planned activity linked to the lead.
        await post("/activities", {
          type: "meeting",
          status: "planned",
          title: actionDetails.meeting_title,
          body: actionDetails.meeting_description,
          starts_at: at(actionDetails.action_date, actionDetails.meeting_time_start),
          ends_at: actionDetails.meeting_time_end ? at(actionDetails.action_date, actionDetails.meeting_time_end) : null,
          subject_type: "lead",
          subject_id: leadId,
        });
      } else {
        // Calls, emails and brochures become tasks with a due time and a reminder.
        const label = NEXT_ACTIONS.find((a) => a.id === action)?.name ?? "Follow up";
        await post("/tasks", {
          title: `${label}: ${company}`,
          due_at: at(actionDetails.action_date, actionDetails.action_time),
          related_type: "lead",
          related_id: leadId,
        });
      }
      notify("Next step scheduled");
      // Re-open the drawer data so the next step shows.
      if (selectedLead?.id === leadId) setSelectedLead((prev) => (prev ? { ...prev } : null));
    } catch (error) {
      notify(apiMessage(error, "Could not schedule the next step."));
    }
  };

  const handleConvert = async (leadId: string, value: number, frequency: string) => {
    try {
      await post(`/leads/${leadId}/convert`, { create_deal: true, deal_amount: value, deal_budget_frequency: frequency });
      notify("Lead converted: account, contact and deal created");
      setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
      fetchLeads();
    } catch (error) {
      notify(apiMessage(error, "Could not convert the lead."));
    }
  };

  const handleConvertToClient = async (leadId: string, contractData: {
    service_type: string;
    start_date: string;
    duration_months: number;
    value?: number;
    billing_cycle: string;
  }) => {
    const lead = leads.find((l) => l.id === leadId);
    try {
      const result = await post<{ data: { account: { id: string } } }>(`/leads/${leadId}/convert`, {});
      notify('Lead converted. Complete its contract setup.');
      const params = new URLSearchParams({ account_id: result.data.account.id, currency: lead?.currency ?? 'UGX', billing_cycle: contractData.billing_cycle, value: String(contractData.value ?? ''), service_type: contractData.service_type, start_date: contractData.start_date, duration_months: String(contractData.duration_months) });
      navigate(`/contracts/new?${params}`);
      setLeads((prev) => prev.filter((l) => l.id !== leadId));
      fetchLeads();
    } catch (error) {
      notify(apiMessage(error, "Could not convert the lead."));
    }
  };

  const handleDeleteClick = (leadId: string) => {
    setLeadToDelete(leadId);
    setShowDeleteConfirm(true);
  };

  const handleBatchDelete = async (rows: { id: string }[]) => {
    if (!await confirmDialog(`Delete ${rows.length} selected leads? This cannot be undone.`)) return;

    const results = await Promise.allSettled(rows.map((r) => del(`/leads/${r.id}`)));
    if (results.some((r) => r.status === "rejected")) fetchLeads();
    const deleted = new Set(rows.filter((_, i) => results[i].status === "fulfilled").map((r) => r.id));
    setLeads((prev: any[]) => prev.filter((item) => !deleted.has(item.id)));
    const failed = rows.length - deleted.size;
    notify(failed ? `${deleted.size} deleted, ${failed} failed` : `${deleted.size} leads deleted successfully`);
  };

  const handleDelete = async (leadId: string) => {
    try {
      await del(`/leads/${leadId}`);
      notify("Lead deleted");
      setLeads((prev) => prev.filter((lead) => lead.id !== leadId));
      if (selectedLead?.id === leadId) {
        setShowLeadDrawer(false);
        setSelectedLead(null);
      }
    } catch (error) {
      notify(apiMessage(error, "Could not delete the lead."));
    }
  };

  const confirmDelete = async () => {
    if (leadToDelete) {
      await handleDelete(leadToDelete);
      setLeadToDelete(null);
    }
  };

  const handleEditLead = (lead: Lead) => {
    setEditingLead(lead);
    setIsEditingLead(true);
    setShowLeadDrawer(false);
    setShowAddLead(true);
  };

  const filteredLeads = leads; // search, status and rating are applied by the API


  const getStageInfo = (stage: number) => {
    return STAGES.find((s) => s.id === stage) || STAGES[0];
  };

  const handleViewLead = (lead: Lead) => {
    setSelectedLead(lead);
    setShowLeadDrawer(true);
  };

  const handleAddLeadSuccess = () => {
    fetchLeads();
    setIsEditingLead(false);
    setEditingLead(null);
  };

  const handleCloseAddLead = () => {
    setShowAddLead(false);
    setIsEditingLead(false);
    setEditingLead(null);
  };

  const columns: ColumnDef<Lead>[] = [
    {
      accessorKey: "company_name",
      header: "Company",
      cell: ({ row }) => (
        <div>
          <p className="text-xs font-semibold text-gray-900">
            {row.original.company_name}
          </p>
          <p className="text-xs text-gray-500 capitalize">
            {row.original.industry?.replace(/_/g, " ")}
          </p>
        </div>
      ),
    },
    {
      accessorKey: "email",
      header: "Contact Person",
      cell: ({ row }) => (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-900">{row.original.contact_person} </p>
          <p className="text-xs text-gray-600">{row.original.email || "—"}</p>
          {row.original.phone && <p className="text-xs text-gray-600">{row.original.phone}</p>}
        </div>
      ),
    },
    {
      accessorKey: 'value',
      header: 'Budget',
      cell: ({ row }) => <span className="text-xs text-gray-700">{row.original.value != null ? `${row.original.currency} ${Number(row.original.value).toLocaleString()}${budgetSuffix(row.original.budget_frequency)}` : '—'}</span>,
    },
    {
      accessorKey: "current_stage",
      header: "Status",
      cell: ({ row }) => {
        const stage = getStageInfo(row.original.current_stage);
        return (
          <span
            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${stage.color}`}
          >
            {stage.name}
          </span>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: "Created",
      cell: ({ row }) => (
        <div>
          <p className="text-xs text-gray-600">
            {new Date(row.original.created_at).toLocaleDateString()}
          </p>
          {row.original.created_by_name && (
            <p className="text-xs text-gray-500">
              owner: {row.original.created_by_name}
            </p>
          )}
        </div>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row }) => (
        <ActionsCell
          onView={() => handleViewLead(row.original)}
          onDelete={() => handleDeleteClick(row.original.id)}
        />
      ),
    },
  ];

  return (
    <div className="min-h-screen p-4 sm:p-0">
      <div className="w-full">
        <PageHeader title="Leads" description="Capture, qualify and convert new business">
          <ViewToggle view={view} onChange={setView} options={[{ id: "board", label: "Board" }, { id: "table", label: "Table" }]} />
          {view === "board" && (
            <button
              onClick={() => setShowAddLead(true)}
              className="px-5 py-1.5 bg-gradient-to-r from-primary to-primary/90 text-white text-sm font-light tracking-wide rounded-full hover:from-black/90 hover:to-black transition-all duration-200  min-w-[140px]"
            >
              Add New Lead
            </button>
          )}
        </PageHeader>

        {view === "board" ? (
          <LeadsBoard refreshKey={boardKey} onOpen={handleViewLead} onMoved={() => { listLoaded.current = false; fetchLeads(); }} />
        ) : (
        <GridProvider id="leads">
        <div className="space-y-4">
        <GridToolbar
          search={<SearchInput className="w-full sm:w-64" placeholder="Search name, company, email or phone…" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />}
          filters={<>
            <label className="text-xs text-gray-600">Status
              <AppSelect className="mt-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">All open leads</option>
                {SELECTABLE_STAGES.map((st) => <option key={st.status} value={st.status}>{st.name}</option>)}
              </AppSelect>
            </label>
            <label className="text-xs text-gray-600">Rating
              <AppSelect className="mt-1 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs" value={ratingFilter} onChange={(e) => setRatingFilter(e.target.value)}>
                <option value="">Any rating</option><option value="hot">Hot</option><option value="warm">Warm</option><option value="cold">Cold</option>
              </AppSelect>
            </label>
          </>}
          activeFilters={(statusFilter !== "all" ? 1 : 0) + (ratingFilter ? 1 : 0)}
          onClearFilters={() => { setStatusFilter("all"); setRatingFilter(""); }}
          sortOptions={[
            { value: "-created_at", label: "Newest" }, { value: "created_at", label: "Oldest" }, { value: "-estimated_value", label: "Highest value" },
            { value: "company_name", label: "Company" }, { value: "first_name", label: "First name" }, { value: "-score", label: "Highest score" },
          ]}
          sort={sort}
          onSort={setSort}
          hasStats
          exportEntity="leads"
          exportPermission="leads.export"
          onAdd={() => setShowAddLead(true)}
          addLabel="Add New Lead"
        />
        <StatsStrip loading={countsLoading} items={[
          { label: "Open leads", value: countText(counts.open), hint: "New, contacted or qualified" },
          { label: "New", value: countText(counts.fresh), hint: "Not contacted yet", tone: counts.fresh ? "warn" : "default" },
          { label: "Qualified", value: countText(counts.qualified), tone: "good" },
          { label: "Converted", value: countText(counts.converted), hint: "Now accounts and deals" },
        ]} />
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          {loading ? (
            <SkeletonRow />
          ) : (
            <>
              <TableProvider columns={columns} data={filteredLeads} onDeleteSelected={handleBatchDelete}>
                <DataTableHeader>
                  {({ headerGroup }) => (
                    <DataTableHeaderGroup headerGroup={headerGroup}>
                      {({ header }) => <DataTableHead header={header} />}
                    </DataTableHeaderGroup>
                  )}
                </DataTableHeader>
                <DataTableBody>
                  {({ row }) => (
                    <DataTableRow
                      row={row}
                      className="hover:bg-gray-50 transition-colors border-b border-gray-100"
                    >
                      {({ cell }) => <DataTableCell cell={cell} />}
                    </DataTableRow>
                  )}
                </DataTableBody>
              </TableProvider>

              {filteredLeads.length === 0 && (
                <div className="text-center py-16">
                  <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Target className="w-8 h-8 text-gray-400" />
                  </div>
                  <h3 className="text-lg font-light text-gray-900 mb-2">
                    No leads found
                  </h3>
                  <p className="text-sm font-light text-gray-500 mb-6 max-w-md mx-auto">
                    {searchQuery || statusFilter !== "all" || ratingFilter
                      ? "Try adjusting your search criteria or filters to find what you're looking for."
                      : "Start building your sales pipeline by adding your first lead."}
                  </p>
                  {!searchQuery && statusFilter === "all" && !ratingFilter && (
                      <button
                        onClick={() => setShowAddLead(true)}
                        className="px-6 py-2 bg-primary text-white text-sm font-light rounded-lg hover:bg-primary/90 transition-all duration-200"
                      >
                        Create First Lead
                      </button>
                    )}
                </div>
              )}

            </>
          )}
          <PaginationBar
            page={pagination.page}
            totalPages={pagination.total_pages}
            total={pagination.total}
            perPage={pagination.per_page}
            onPage={handlePageChange}
            onPerPage={(n) => setPagination((prev) => ({ ...prev, per_page: n, page: 1 }))}
            noun="leads"
          />
        </div>
        </div>
        </GridProvider>
        )}
      </div>

      <LeadDrawer
        lead={selectedLead}
        isOpen={showLeadDrawer}
        onClose={() => {
          setShowLeadDrawer(false);
          setSelectedLead(null);
        }}
        onStageChange={handleStageChange}
        onNextActionChange={handleNextActionChange}
        onConvert={handleConvert}
        onConvertToClient={handleConvertToClient}
        onDelete={handleDelete}
        onEdit={handleEditLead}
      />

      <AddLeadForm
        isOpen={showAddLead}
        onClose={handleCloseAddLead}
        onSuccess={handleAddLeadSuccess}
        editLead={editingLead}
        isEditing={isEditingLead}
      />

      <ConfirmationPopup
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setLeadToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Lead"
        message="Are you sure you want to delete this lead? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  );
}

export default LeadsPage;
